import React from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { ErrorCard } from './ui/StatePanel';
import { Skeleton } from './ui/skeleton';
import { Rise } from './ui/motion';
import './RouteErrorBoundary.css';

/**
 * Wraps lazy-loaded routes so that a failed chunk import (or any unhandled
 * render error) doesn't leave the user staring at an empty layout.
 *
 * The most common production failure here is a "stale chunk" — the browser
 * has the old build's webpack runtime in memory, the user navigates after a
 * deploy, and the lazy `import()` 404s on a chunk hash that no longer exists.
 * Suspense then throws an error that nothing in the tree catches, so the
 * route silently renders nothing. Refreshing fixes it (because that fetches
 * the new index.html with the new chunk hashes), which is exactly the
 * symptom users have been reporting.
 *
 * Strategy:
 *   1. Detect chunk-load errors specifically and reload once automatically
 *      (with a sessionStorage flag to prevent infinite reload loops).
 *   2. For any other error, show a small inline retry card so the user
 *      can recover without losing the rest of the app shell.
 */

type Props = {
  children: React.ReactNode;
  /** Optional key (e.g. location.pathname) — when this changes the boundary
   *  clears any stuck error so a sidebar nav can recover from a previous
   *  failure without the user having to click "Try again". */
  resetKey?: string;
};
type State = { error: Error | null; lastResetKey?: string };

const RELOAD_FLAG = 'lanura:route-chunk-reload-attempted';

/* The "reload once on a chunk error" guard has to survive a reload, which
 * rules out an in-memory flag — a new document resets it. sessionStorage is
 * the natural home, but it THROWS when storage is blocked (strict privacy
 * settings, blocked cookies, some incognito/ITP configurations). The previous
 * catch branch reloaded unconditionally in that case, so every chunk error
 * reloaded again, and again: an infinite refresh loop for exactly the users
 * least able to diagnose it.
 *
 * window.name is the fallback: it persists across same-origin reloads in the
 * same tab and is not subject to storage permissions. A token is appended and
 * stripped rather than overwriting, so anything else using window.name is left
 * intact. localStorage is tried in between since it may be allowed where
 * sessionStorage is not. */
const NAME_TOKEN = '~lanura-chunk-reload:';

/* The mark EXPIRES rather than being cleared by anything.
 *
 * The previous version cleared it in componentDidMount, on the theory that a
 * successful mount proved the new chunks had loaded. It proved nothing: this
 * boundary mounts on every document load — including the one immediately after
 * a chunk-error reload — and it mounts BEFORE its lazy children resolve. So the
 * guard was wiped microseconds before the same error recurred, "reload once"
 * became reload forever, and /login refreshed itself roughly every 18 seconds.
 *
 * A timestamp removes the need for anyone to clear it correctly. Within the
 * window a second failure renders the error card instead of reloading; outside
 * it, a genuinely new stale-chunk episode hours later still gets its one free
 * reload. There is no code path that can reopen the guard early, which is the
 * property the previous design lacked. */
const RELOAD_WINDOW_MS = 60_000;

function readMarkAge(raw: string | null | undefined): number | null {
  const t = Number(String(raw ?? '').trim());
  if (!Number.isFinite(t) || t <= 0) return null;
  return Date.now() - t;
}

function hasReloadAttempted(): boolean {
  const fresh = (age: number | null) => age !== null && age >= 0 && age < RELOAD_WINDOW_MS;
  try {
    if (fresh(readMarkAge(sessionStorage.getItem(RELOAD_FLAG)))) return true;
  } catch {
    /* blocked */
  }
  try {
    if (fresh(readMarkAge(localStorage.getItem(RELOAD_FLAG)))) return true;
  } catch {
    /* blocked */
  }
  try {
    const m = String(window.name || '').match(/~lanura-chunk-reload:(\d+)~/);
    return fresh(readMarkAge(m?.[1]));
  } catch {
    return false;
  }
}

function markReloadAttempted(): void {
  const now = String(Date.now());
  try {
    sessionStorage.setItem(RELOAD_FLAG, now);
  } catch {
    /* blocked */
  }
  try {
    localStorage.setItem(RELOAD_FLAG, now);
  } catch {
    /* blocked */
  }
  try {
    const n = String(window.name || '').replace(/~lanura-chunk-reload:\d+~/, '');
    window.name = n + NAME_TOKEN + now + '~';
  } catch {
    /* nothing left to try */
  }
}

/** Only ever called from the user's own "Refresh page" click. A deliberate
 *  action deserves a fresh attempt; nothing automatic may call this. */
function clearReloadAttempted(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
  try {
    window.name = String(window.name || '').replace(/~lanura-chunk-reload:\d+~/, '');
  } catch {
    /* ignore */
  }
}

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: string; message?: string; code?: string };
  const name = String(e.name || '');
  const msg = String(e.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /Failed to (fetch|import|load).*module/i.test(msg) ||
    /Module .* failed/i.test(msg) ||
    /Dynamic import/i.test(msg) ||
    /import\(\) failed/i.test(msg)
  );
}

class RouteErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // If the parent passes a new resetKey (typically the route pathname),
    // clear any stored error so the next route can render fresh.
    if (props.resetKey !== undefined && state.lastResetKey !== props.resetKey) {
      return { error: null, lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    // Always report — we don't have telemetry on what's causing the blank-screen
    // bugs in the wild, and this is the boundary that catches them.
    try {
      posthog?.capture('route_render_error', {
        error_name: String(error?.name || ''),
        error_message: String(error?.message || '').slice(0, 500),
        error_stack: String(error?.stack || '').slice(0, 1000),
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        is_chunk_error: isChunkLoadError(error),
      });
    } catch {}

    if (isChunkLoadError(error)) {
      // Auto-reload once on chunk load failure, so a user on a stale build
      // silently picks up the new one. The guard must hold even when storage
      // is blocked — otherwise "reload once" becomes "reload forever" and the
      // page never settles. When the guard is already set we fall through and
      // render the error UI instead, which at least gives the user a way out.
      if (!hasReloadAttempted()) {
        markReloadAttempted();
        window.location.reload();
      }
    } else {
      // Non-chunk errors: log for diagnostics; the fallback UI handles UX.
      // eslint-disable-next-line no-console
      console.error('Route render error:', error);
    }
  }

  /* Deliberately NO componentDidMount clearing the guard. Mounting is not
   * evidence the chunks loaded — this boundary mounts before its lazy children
   * resolve, and on the very load that follows a chunk-error reload. Clearing
   * here is what turned "reload once" into an endless refresh. The mark now
   * expires on its own; see RELOAD_WINDOW_MS. */

  handleRetry = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    clearReloadAttempted();
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Chunk load errors trigger a reload in componentDidCatch; render a
    // minimal placeholder while the reload happens so the user doesn't see a
    // jarring error card mid-reload. A shimmer bar makes the wait read as
    // loading rather than as a crash.
    if (isChunkLoadError(error)) {
      return (
        <div className="route-reload" role="status" aria-live="polite">
          <Skeleton className="route-reload__bar" aria-hidden="true" />
          <span className="route-reload__text">Loading the latest version&hellip;</span>
        </div>
      );
    }

    return (
      <Rise as="div" className="route-error">
        <ErrorCard
          icon={<AlertTriangle className="lnr-icon" aria-hidden="true" />}
          title={<>This page didn&rsquo;t load</>}
          description="Something went wrong rendering this view. Try again, or refresh the page if the problem keeps happening."
          action={
            <>
              <Button type="button" size="md" onClick={this.handleRetry}>
                Try again
              </Button>
              <Button type="button" variant="outline" size="md" onClick={this.handleReload}>
                Refresh page
              </Button>
            </>
          }
        />
      </Rise>
    );
  }
}

/** Functional wrapper that automatically passes the current pathname as the
 *  reset key — drop this anywhere lazy routes are rendered. */
const RouteErrorBoundaryWithLocation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <RouteErrorBoundary resetKey={location.pathname}>{children}</RouteErrorBoundary>;
};

export default RouteErrorBoundaryWithLocation;
export { RouteErrorBoundary };
